import { useState, useEffect, useCallback } from 'react';
import { FileText, Search, RefreshCw, Link2, ChevronLeft, ChevronRight, AlertTriangle, Loader2, CheckCircle2, Clock } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import api from '../lib/api';
import { toast } from 'sonner';
import { DteDocsActions } from '../components/dte/DteDocsActions'; // Iter 235.e
import { AlegraStatusBadge } from '../components/dte/AlegraStatusBadge'; // Iter 235.e
import { ProveedorHistorialModal } from '../components/dte/ProveedorHistorialModal'; // Iter 235.e
import { StickyHScroll } from '../components/ui/sticky-hscroll'; // Iter 235.g
import { fmtFecha } from '../lib/utils'; // Iter 292.g — formato DD-MM-YYYY canónico

const fmt = (num) => Math.round(num || 0).toLocaleString('es-CL');

function getDateRange(preset) {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth();
  const d = today.getDate();
  const pad = (n) => String(n).padStart(2, '0');
  const fmtD = (dt) => `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}`;
  const todayStr = fmtD(today);

  switch (preset) {
    case 'today': return { desde: todayStr, hasta: todayStr };
    case 'yesterday': { const yd = new Date(y, m, d-1); return { desde: fmtD(yd), hasta: fmtD(yd) }; }
    case 'this_week': { const dow = today.getDay() || 7; const mon = new Date(y, m, d - dow + 1); return { desde: fmtD(mon), hasta: todayStr }; }
    case 'last_week': { const dow = today.getDay() || 7; const mon = new Date(y, m, d - dow - 6); const sun = new Date(y, m, d - dow); return { desde: fmtD(mon), hasta: fmtD(sun) }; }
    case 'this_month': return { desde: `${y}-${pad(m+1)}-01`, hasta: todayStr };
    case 'last_month': { const lm = new Date(y, m, 0); const fm = new Date(y, m-1, 1); return { desde: fmtD(fm), hasta: fmtD(lm) }; }
    case 'this_quarter': { const qm = Math.floor(m/3)*3; return { desde: `${y}-${pad(qm+1)}-01`, hasta: todayStr }; }
    case 'last_quarter': { const qm = Math.floor(m/3)*3; const lqe = new Date(y, qm, 0); const lqs = new Date(y, qm-3, 1); return { desde: fmtD(lqs), hasta: fmtD(lqe) }; }
    case 'this_year': return { desde: `${y}-01-01`, hasta: todayStr };
    case 'last_year': return { desde: `${y-1}-01-01`, hasta: `${y-1}-12-31` };
    default: return { desde: '', hasta: '' };
  }
}

const PERIODOS = [
  { val: '', label: 'Desde el Inicio' },
  { val: 'today', label: 'Hoy' },
  { val: 'yesterday', label: 'Ayer' },
  { val: 'this_week', label: 'Esta Semana' },
  { val: 'last_week', label: 'Semana Anterior' },
  { val: 'this_month', label: 'Este Mes' },
  { val: 'last_month', label: 'Mes Anterior' },
  { val: 'this_quarter', label: 'Este Trimestre' },
  { val: 'last_quarter', label: 'Trimestre Anterior' },
  { val: 'this_year', label: 'Este Año' },
  { val: 'last_year', label: 'Año Anterior' },
  { val: 'custom', label: 'Personalizado' },
];

const TABS = [
  { tipo: '33', label: 'Facturas', color: 'blue', badge: 'bg-blue-100 text-blue-700 border-blue-200' },
  { tipo: '61', label: 'Notas de Crédito', color: 'red', badge: 'bg-red-100 text-red-700 border-red-200' },
  { tipo: '56', label: 'Notas de Débito', color: 'orange', badge: 'bg-orange-100 text-orange-700 border-orange-200' },
  { tipo: '52', label: 'Guías de Despacho', color: 'gray', badge: 'bg-gray-100 text-gray-600 border-gray-200' },
];

export default function RegistroDTE() {
  const [activeTab, setActiveTab] = useState('33');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [asociarModal, setAsociarModal] = useState({ open: false, folio: null, tipo: null });
  const [provHistModal, setProvHistModal] = useState({ open: false, rut: null }); // Iter 235.e
  const [folioFactura, setFolioFactura] = useState('');
  const [asociando, setAsociando] = useState(false);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [periodo, setPeriodo] = useState('');
  const [showCustomDates, setShowCustomDates] = useState(false);
  const [soloSinContab, setSoloSinContab] = useState(false);
  const [saldoResult, setSaldoResult] = useState(null);
  const [sinContab, setSinContab] = useState(0);
  const [sinContab15d, setSinContab15d] = useState(0);
  const LIMIT = 50;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/contabilidad/registro-dte', {
        params: {
          tipo: activeTab, skip: page * LIMIT, limit: LIMIT, search,
          fecha_desde: fechaDesde, fecha_hasta: fechaHasta,
          solo_sin_contabilizar: soloSinContab,
          orden: soloSinContab ? 'asc' : 'desc',
        },
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setStats(res.data.stats || {});
      setSinContab(res.data.sin_contabilizar || 0);
      setSinContab15d(res.data.sin_contabilizar_15d || 0);
    } catch (err) {
      toast.error('Error cargando DTEs');
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, search, fechaDesde, fechaHasta, soloSinContab]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleTabChange = (tipo) => {
    setActiveTab(tipo);
    setPage(0);
    setSearch('');
    setFechaDesde('');
    setFechaHasta('');
    setPeriodo('');
    setShowCustomDates(false);
    setSoloSinContab(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    loadData();
  };

  const handlePeriodoChange = (val) => {
    setPeriodo(val);
    setPage(0);
    if (val === 'custom') {
      setShowCustomDates(true);
      return;
    }
    setShowCustomDates(false);
    const range = getDateRange(val);
    setFechaDesde(range.desde);
    setFechaHasta(range.hasta);
  };

  const handleAsociar = async () => {
    if (!folioFactura.trim()) return;
    setAsociando(true);
    setSaldoResult(null);
    try {
      const endpoint = asociarModal.tipo === '52'
        ? '/contabilidad/registro-dte/asociar-gd-factura'
        : '/contabilidad/registro-dte/asociar-factura';
      const body = asociarModal.tipo === '52'
        ? { folio_gd: asociarModal.folio, folio_factura: folioFactura.trim() }
        : { folio_nc: asociarModal.folio, folio_factura: folioFactura.trim() };
      const res = await api.post(endpoint, body);
      toast.success(res.data.message);
      if (res.data.saldo_neto !== undefined) {
        setSaldoResult({
          factura_total: res.data.factura_total,
          nc_total: res.data.nc_total,
          saldo_neto: res.data.saldo_neto,
        });
      } else {
        setAsociarModal({ open: false, folio: null, tipo: null });
        setFolioFactura('');
        loadData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al asociar');
    } finally {
      setAsociando(false);
    }
  };

  const activeTabConfig = TABS.find(t => t.tipo === activeTab);
  const totalPages = Math.ceil(total / LIMIT);
  const isNC = activeTab === '61';
  const isND = activeTab === '56';
  const isGD = activeTab === '52';

  return (
    <div className="space-y-5" data-testid="registro-dte-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <FileText className="w-6 h-6 text-indigo-600" /> Registro de DTEs
        </h1>
        <p className="text-sm text-muted-foreground">Consulte todos los documentos tributarios recibidos, separados por tipo.</p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {TABS.map(tab => {
          const s = stats[tab.tipo] || { count: 0, total: 0 };
          return (
            <button key={tab.tipo}
              onClick={() => handleTabChange(tab.tipo)}
              data-testid={`tab-${tab.tipo}`}
              className={`rounded-lg border-2 p-3 text-left transition-all ${
                activeTab === tab.tipo
                  ? `border-${tab.color}-500 bg-${tab.color}-50 shadow-sm`
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}>
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border ${tab.badge}`}>
                  {tab.label}
                </span>
                <span className="text-lg font-bold text-gray-900">{s.count}</span>
              </div>
              <p className={`text-xs mt-1 ${tab.tipo === '61' ? 'text-red-600' : 'text-gray-500'}`}>
                {tab.tipo === '61' ? '-' : ''}${fmt(s.total)}
              </p>
            </button>
          );
        })}
      </div>

      {/* Search and info bar */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <form onSubmit={handleSearch} className="flex-1 min-w-[200px] max-w-md">
            <label className="block text-xs font-medium mb-1 text-gray-600">Buscar en {activeTabConfig?.label}</label>
            <div className="relative flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input placeholder="Folio, proveedor, RUT o N. OC..."
                  value={search} onChange={e => setSearch(e.target.value)}
                  className="pl-9" data-testid="search-registro-dte" />
              </div>
              <Button type="submit" variant="outline" size="sm" data-testid="search-btn">Buscar</Button>
            </div>
          </form>

          {/* Período selector */}
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600">Período</label>
            <select
              value={periodo}
              onChange={e => handlePeriodoChange(e.target.value)}
              className="h-9 rounded border border-gray-200 bg-white px-3 text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              data-testid="periodo-select"
            >
              {PERIODOS.map(p => (
                <option key={p.val} value={p.val}>{p.label}</option>
              ))}
            </select>
          </div>

          {/* Custom date range */}
          {showCustomDates && (
            <div className="flex gap-2 items-end">
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-600">Desde</label>
                <Input type="date" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)}
                  className="w-[140px] text-xs" data-testid="fecha-desde" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-600">Hasta</label>
                <Input type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)}
                  className="w-[140px] text-xs" data-testid="fecha-hasta" />
              </div>
              <Button variant="default" size="sm" onClick={() => { setPage(0); loadData(); }}
                className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="apply-dates">
                Aplicar
              </Button>
            </div>
          )}

          {/* Sin Contabilizar filter */}
          {!isGD && (
            <button
              onClick={() => { setSoloSinContab(!soloSinContab); setPage(0); }}
              className={`h-9 px-3 rounded border text-xs font-medium transition-all ${
                soloSinContab
                  ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-amber-400 hover:bg-amber-50'
              }`}
              data-testid="filter-sin-contab"
            >
              Sin Contabilizar ({sinContab})
            </button>
          )}

          <Button variant="outline" onClick={loadData} disabled={loading} data-testid="refresh-registro-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <div className="text-xs text-gray-500">
            Mostrando {items.length} de {total} {activeTabConfig?.label?.toLowerCase()}
            {soloSinContab && ' (solo sin contabilizar, más antigua primero)'}
          </div>
        </div>

        {/* Info banners per type */}
        {(sinContab > 0 && !isGD) && (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-2 flex items-center gap-2 text-xs text-amber-700" data-testid="banner-sin-contab">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span><strong>{sinContab}</strong> {activeTabConfig?.label?.toLowerCase()} sin contabilizar en Alegra{sinContab15d > 0 && <>, de las cuales <strong className="text-red-600">{sinContab15d} llevan más de 15 días</strong></>}.</span>
          </div>
        )}
        {isNC && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded p-2 flex items-center gap-2 text-xs text-red-700">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            Las Notas de Crédito representan montos que el proveedor nos debe o descuentos a facturas existentes. Asócielas a la factura correspondiente.
          </div>
        )}
        {isND && (
          <div className="mt-3 bg-orange-50 border border-orange-200 rounded p-2 flex items-center gap-2 text-xs text-orange-700">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            Las Notas de Débito aumentan el monto adeudado al proveedor. Verifique y asócielas a la factura original.
          </div>
        )}
        {isGD && (
          <div className="mt-3 bg-gray-100 border border-gray-200 rounded p-2 flex items-center gap-2 text-xs text-gray-600">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            Las Guías de Despacho son solo referenciales. Los montos pueden no ser exactos. Espere la factura para contabilizar. Puede asociarlas a la factura cuando llegue.
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm">
            No se encontraron {activeTabConfig?.label?.toLowerCase() || 'documentos'}
          </div>
        ) : (
          <StickyHScroll>
            <table className="w-full text-xs" data-testid="registro-dte-table">
              <thead>
                <tr className="bg-gray-800 text-white text-left">
                  <th className="px-2 py-2 text-center w-8" title="Estado contabilización"></th>
                  <th className="px-3 py-2">Folio</th>
                  <th className="px-2 py-2 text-center">Tipo</th>
                  <th className="px-3 py-2">Fecha</th>
                  <th className="px-3 py-2">Proveedor</th>
                  <th className="px-3 py-2">RUT</th>
                  <th className="px-3 py-2 text-right">Neto</th>
                  <th className="px-3 py-2 text-right">IVA</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-center">OC</th>
                  <th className="px-3 py-2 text-center">F. Asociada</th>
                  <th className="px-3 py-2 text-center">Estado</th>
                  <th className="px-3 py-2 text-center" title="Visualizar Factura · OC · Recepción">Docs</th>
                  {(isNC || isND) && <th className="px-3 py-2 text-right">Saldo Neto</th>}
                  {(isNC || isND || isGD) && <th className="px-3 py-2 text-center">Acción</th>}
                </tr>
              </thead>
              <tbody>
                {items.map((d, i) => {
                  const over15 = !d.contabilizada && d.dias_sin_contabilizar > 15;
                  return (
                  <tr key={`${d.folio}-${i}`}
                    className={`border-t hover:bg-gray-50/50 ${
                      over15 ? 'bg-amber-50/60' :
                      isNC ? 'bg-red-50/20' : isND ? 'bg-orange-50/20' : isGD ? 'bg-gray-50/30' : ''
                    }`}
                    data-testid={`registro-row-${d.folio}`}>
                    <td className="px-2 py-2 text-center" data-testid={`contab-status-${d.folio}`}>
                      {d.contabilizada_en_alegra ? (
                        <CheckCircle2 className="w-4 h-4 text-blue-600 mx-auto" />
                      ) : d.contabilizada ? (
                        <CheckCircle2 className="w-4 h-4 text-purple-500 mx-auto" />
                      ) : over15 ? (
                        <Clock className="w-4 h-4 text-amber-500 mx-auto animate-pulse" title={`${d.dias_sin_contabilizar} días sin contabilizar`} />
                      ) : (
                        <span className="inline-block w-2 h-2 rounded-full bg-gray-300 mx-auto" title="Sin contabilizar" />
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono font-bold text-indigo-700">{d.folio}</td>
                    <td className="px-2 py-2 text-center">
                      {d.tipo_dte === '61' ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-100 text-red-700 border border-red-200">NC</span>
                      ) : d.tipo_dte === '56' ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-orange-100 text-orange-700 border border-orange-200">ND</span>
                      ) : d.tipo_dte === '52' ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-gray-100 text-gray-500 border border-gray-200">GD</span>
                      ) : (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-blue-50 text-blue-600">Fact</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                      {fmtFecha(d.fecha_emision) || '-'}
                      {over15 && (
                        <span className="ml-1 text-[9px] text-amber-600 font-bold">({d.dias_sin_contabilizar}d)</span>
                      )}
                    </td>
                    <td className="px-3 py-2 truncate max-w-[220px]" title={`${d.emisor_razon_social}${d.emisor_direccion ? `\n${d.emisor_direccion}` : ''}${d.emisor_comuna ? ` · ${d.emisor_comuna}` : ''}${d.emisor_ciudad && d.emisor_ciudad !== d.emisor_comuna ? ` · ${d.emisor_ciudad}` : ''}${d.medio_pago ? `\nForma pago: ${d.medio_pago}` : ''}${d.fecha_vencimiento ? `\nVence: ${fmtFecha(d.fecha_vencimiento)}` : ''}`}>
                      <div className="flex flex-col gap-0">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate">{d.emisor_razon_social}</span>
                          {d.origen === 'rcv_provisional' && !d.tiene_xml && (
                            <span
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-800 border border-amber-300 shrink-0"
                              title={`DTE Provisional desde RCV ${d.rcv_periodo || ''}. XML no descargado aún.`}
                              data-testid={`badge-provisional-${d.folio}`}
                            >
                              📋 RCV
                            </span>
                          )}
                          {d.origen === 'rcv_reconciliado' && (
                            <span
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 shrink-0"
                              title={`Reconciliado: era provisional desde RCV ${d.rcv_periodo || ''}, ahora con XML.`}
                            >
                              ✅ Reconc.
                            </span>
                          )}
                          {d.verificado_en_rcv && d.origen !== 'rcv_provisional' && d.origen !== 'rcv_reconciliado' && (
                            <span
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-blue-50 text-blue-700 border border-blue-200 shrink-0"
                              title={`Verificado en RCV período ${d.rcv_periodo || ''}.`}
                            >
                              🔍 RCV
                            </span>
                          )}
                        </div>
                        {/* Iter 235.c (Carlos): segunda línea con comuna + medio_pago + vencimiento */}
                        {(d.emisor_comuna || d.medio_pago || d.fecha_vencimiento) && (
                          <div className="flex items-center gap-2 text-[9px] text-gray-500 mt-0.5">
                            {d.emisor_comuna && (
                              <span className="truncate" title={`${d.emisor_direccion || ''} · ${d.emisor_comuna}${d.emisor_ciudad ? ` · ${d.emisor_ciudad}` : ''}`}>
                                📍 {d.emisor_comuna}
                              </span>
                            )}
                            {d.medio_pago && (
                              <span className="text-blue-600 truncate max-w-[90px]" title={`Forma pago: ${d.medio_pago}`}>
                                💳 {d.medio_pago}
                              </span>
                            )}
                            {d.fecha_vencimiento && (
                              <span className="text-amber-700 font-medium" title="Fecha vencimiento DTE">
                                ⏰ {fmtFecha(d.fecha_vencimiento)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 font-mono text-[10px]">
                      <button
                        className="text-indigo-600 hover:text-indigo-800 hover:underline"
                        onClick={() => setProvHistModal({ open: true, rut: d.emisor_rut })}
                        title="Ver historial completo del proveedor"
                        data-testid={`prov-link-${d.folio}`}
                      >
                        {d.emisor_rut}
                      </button>
                    </td>
                    <td className={`px-3 py-2 text-right font-mono ${isNC ? 'text-red-600' : ''}`}>
                      {isNC ? '-' : ''}${fmt(d.neto)}
                    </td>
                    <td className={`px-3 py-2 text-right font-mono ${isNC ? 'text-red-600' : ''}`}>
                      {isNC ? '-' : ''}${fmt(d.iva)}
                    </td>
                    <td className={`px-3 py-2 text-right font-mono font-medium ${isNC ? 'text-red-600 font-bold' : ''}`}>
                      {isNC ? '-' : ''}${fmt(d.total)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {d.orden_compra ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700">
                          {d.orden_compra}
                        </span>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {d.factura_asociada ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-100 text-green-700 border border-green-200">
                          <Link2 className="w-3 h-3 mr-0.5" /> {d.factura_asociada}
                        </span>
                      ) : (isNC || isND || isGD) ? (
                        <span className="text-[10px] text-amber-600 font-medium">Sin asociar</span>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    {/* Iter 235.e (Carlos): Estado claro (PAGADA / EN ALEGRA / CONTAB. ERP / DTE RECIBIDO) */}
                    <td className="px-3 py-2 text-center">
                      <AlegraStatusBadge
                        contabilizada_en_alegra={d.contabilizada_en_alegra}
                        contabilizada={d.contabilizada}
                        estado_pago={d.estado_pago}
                        alegra_bill_id={d.alegra_bill_id}
                        en_nomina={d.en_nomina}
                        liberada_para_pago={d.liberada_para_pago}
                      />
                    </td>
                    {/* Iter 235.e (Carlos): Docs — DTE / OC / Recep */}
                    <td className="px-3 py-2 text-center">
                      <DteDocsActions
                        folio={d.folio}
                        rutEmisor={d.emisor_rut}
                        numeroOc={d.numero_oc}
                        numeroRecepcion={d.numero_recepcion}
                      />
                    </td>
                    {(isNC || isND) && (
                      <td className="px-3 py-2 text-right font-mono">
                        {d.saldo_neto !== null && d.saldo_neto !== undefined ? (
                          <div>
                            <span className="text-green-700 font-bold">${fmt(d.saldo_neto)}</span>
                            <p className="text-[9px] text-gray-400">Fact: ${fmt(d.factura_total)}</p>
                          </div>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                    )}
                    {(isNC || isND || isGD) && (
                      <td className="px-3 py-2 text-center">
                        {!d.factura_asociada && (
                          <button
                            onClick={() => { setAsociarModal({ open: true, folio: d.folio, tipo: d.tipo_dte }); setFolioFactura(''); }}
                            className="inline-flex items-center gap-0.5 px-2 py-1 rounded text-[10px] font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors border border-blue-200"
                            data-testid={`asociar-btn-${d.folio}`}>
                            <Link2 className="w-3 h-3" /> Asociar
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </StickyHScroll>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t px-4 py-3 bg-gray-50">
            <p className="text-xs text-gray-500">
              Página {page + 1} de {totalPages}
            </p>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" disabled={page === 0}
                onClick={() => setPage(p => p - 1)} data-testid="prev-page">
                <ChevronLeft className="w-4 h-4" /> Anterior
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages - 1}
                onClick={() => setPage(p => p + 1)} data-testid="next-page">
                Siguiente <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Association Modal */}
      <Dialog open={asociarModal.open} onOpenChange={(o) => { if (!o) { setAsociarModal({ open: false, folio: null, tipo: null }); setFolioFactura(''); setSaldoResult(null); loadData(); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-blue-600" />
              Asociar {asociarModal.tipo === '52' ? 'Guía de Despacho' : asociarModal.tipo === '61' ? 'Nota de Crédito' : 'Nota de Débito'} a Factura
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-gray-50 border rounded p-3">
              <p className="text-xs text-gray-500">Documento origen</p>
              <p className="text-sm font-bold text-indigo-700">Folio {asociarModal.folio}</p>
            </div>

            {saldoResult ? (
              <div className="space-y-3" data-testid="saldo-result">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-xs text-green-600 mb-1">Asociación exitosa - Saldo Neto</p>
                  <p className="text-2xl font-bold text-green-700">${fmt(saldoResult.saldo_neto)}</p>
                  <div className="flex justify-center gap-4 mt-2 text-xs text-gray-500">
                    <span>Factura: ${fmt(saldoResult.factura_total)}</span>
                    <span>-</span>
                    <span className="text-red-600">NC/ND: ${fmt(saldoResult.nc_total)}</span>
                  </div>
                </div>
                <Button className="w-full" onClick={() => { setAsociarModal({ open: false, folio: null, tipo: null }); setFolioFactura(''); setSaldoResult(null); loadData(); }}
                  data-testid="close-saldo">
                  Cerrar
                </Button>
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium mb-1">N° Folio de la Factura asociada</label>
                  <Input
                    value={folioFactura}
                    onChange={e => setFolioFactura(e.target.value)}
                    placeholder="Ej: 12345678"
                    data-testid="input-folio-asociar"
                  />
                  <p className="text-[10px] text-gray-500 mt-1">
                    {asociarModal.tipo === '52'
                      ? 'Ingrese el folio de la factura que corresponde a esta guía de despacho'
                      : 'Ingrese el folio de la factura que esta NC/ND modifica'}
                  </p>
                </div>
              </>
            )}
          </div>
          {!saldoResult && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setAsociarModal({ open: false, folio: null, tipo: null })} data-testid="cancel-asociar">
                Cancelar
              </Button>
              <Button onClick={handleAsociar} disabled={!folioFactura.trim() || asociando}
                className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-asociar">
                {asociando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
                Asociar
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Iter 235.e (Carlos): Modal historial proveedor */}
      <ProveedorHistorialModal
        open={provHistModal.open}
        rut={provHistModal.rut}
        onClose={() => setProvHistModal({ open: false, rut: null })}
      />
    </div>
  );
}
